import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setShowPreviewImages } from "@app/player/playerSlice";

import "../styles/PreviewImages.scss";

const IMAGE_BOX_WIDTH = Math.abs(window.innerWidth - 1920) <= 96 ? 330 : 220;
const PROGRESS_BAR_WIDTH =
  Math.abs(window.innerWidth - 1920) <= 96 ? 1380 : 920;

const PreviewImages = ({
  currentChannel,
  currentArchive,
  currentStep,
  totalSteps,
  showPreviewImages,
}) => {
  const timeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const preloadImageRef = useRef(null);
  const dispatch = useDispatch();

  const [displayedImageUrl, setDisplayedImageUrl] = useState("");
  const [hasError, setHasError] = useState(false);

  const getArchiverHost = useCallback(() => {
    const archiverIp = currentChannel?.archived_channel?.archiver?.ip;
    if (archiverIp) {
      return `${archiverIp}/screenshots`;
    }
    return "https://archiver.tctv.ge/screenshots";
  }, [currentChannel]);

  const generateImagePath = useCallback(
    ({ year, month, day, hour, channel_id, step }) => {
      const formatNumber = (num) => (num < 10 ? `0${num}` : `${num}`);

      let adjustedHour = hour;
      let adjustedStep = step;

      if (step > 59) {
        adjustedHour = hour + Math.floor(step / 60);
        adjustedStep = step % 60;
      }

      const formattedMonth = formatNumber(month);
      const formattedDay = formatNumber(day);
      const formattedHour = formatNumber(adjustedHour);
      const formattedStep =
        adjustedStep === 0 ? "01" : formatNumber(Math.floor(adjustedStep));

      const host = getArchiverHost();
      return `${host}/${channel_id}/${year}/${formattedMonth}/${formattedDay}/${formattedHour}/${formattedStep}.jpg`;
    },
    [getArchiverHost]
  );

  const preloadImage = useCallback((url) => {
    if (preloadImageRef.current) {
      preloadImageRef.current.onload = null;
      preloadImageRef.current.onerror = null;
    }

    const img = new Image();
    preloadImageRef.current = img;

    img.onload = () => {
      setDisplayedImageUrl(url);
      setHasError(false);
    };

    img.onerror = () => {
      setHasError(true);
    };

    img.src = url;
  }, []);

  const updateImageUrl = useCallback(
    ({ year, month, day, hour, channel_id, step }) => {
      const path = generateImagePath({
        year,
        month: parseInt(month),
        day: parseInt(day),
        hour: parseInt(hour),
        channel_id,
        step: step,
      });

      preloadImage(path);
    },
    [generateImagePath, preloadImage]
  );

  const getDate = useCallback(
    (archive) => {
      const start = archive.start_ut;
      const start_date = new Date(start * 1000);

      const params = {
        year: start_date.getFullYear(),
        month: start_date.getMonth() + 1,
        day: start_date.getDate(),
        hour: start_date.getUTCHours(),
        channel_id: currentChannel.id,
        step: currentStep,
      };

      updateImageUrl(params);
    },
    [currentChannel?.id, currentStep, updateImageUrl]
  );

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (currentArchive) {
        getDate(currentArchive);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [currentArchive, getDate]);

  useEffect(() => {
    if (showPreviewImages) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = setTimeout(() => {
        dispatch(setShowPreviewImages(false));
      }, 5000);
    }

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [showPreviewImages, dispatch]);

  const getBoxPosition = () => {
    try {
      // Calculate the maximum position where the full image is still visible
      const maxPosition =
        ((PROGRESS_BAR_WIDTH - IMAGE_BOX_WIDTH) / PROGRESS_BAR_WIDTH) * 100;

      // Calculate steps for full image width
      const stepsForImage = Math.ceil(
        (IMAGE_BOX_WIDTH / PROGRESS_BAR_WIDTH) * totalSteps
      );

      // Don't move if we're at the start
      if (currentStep <= stepsForImage / 2) {
        return 0;
      }

      // Stop at the end to keep full image visible
      if (currentStep >= totalSteps - stepsForImage / 2) {
        return maxPosition;
      }

      // Calculate position for steps between start and end
      const availableSteps = totalSteps - stepsForImage;
      const currentStepInRange = currentStep - stepsForImage / 2;
      const progressPercent = (currentStepInRange / availableSteps) * 100;

      // Return position clamped between 0 and maxPosition
      return Math.min(
        maxPosition,
        Math.max(0, (maxPosition * progressPercent) / 100)
      );
    } catch (error) {
      console.error("Error in getBoxPosition:", error);
      return 0;
    }
  };

  return (
    <>
      {displayedImageUrl && !hasError && (
        <div
          className={`preview-images_wrapper ${showPreviewImages ? "visible" : ""}`}
          style={{ height: `${(22 / 16) * 9}rem` }}
        >
          <div
            className="preview-images_image_wrapper"
            style={{
              left: `${getBoxPosition()}%`,
              transform: "translateX(0)",
              position: "absolute",
            }}
          >
            <img
              src={displayedImageUrl}
              alt="preview"
              className="preview-images_image"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PreviewImages;
